// =====================================================================
// Landing, public marketing page. Mirrors the original Liquid Memory.html
// landing markup 1:1 (same class names, same structure) so the original
// CSS keeps working unchanged. External links use real <a target="_blank">
// tags with rel="noopener noreferrer"; in-app routes use react-router's
// <Link>.
// =====================================================================

import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import SEO from "../components/SEO.jsx";
import SiteNav from "../components/SiteNav.jsx";
import { HEADLINE_BILL_REDUCTION_PCT } from "../lib/claims.js";
// PRICING_TIERS is the same constant the /pricing page and the
// portal billing dashboard read from. Importing here means the
// pricing card grid stays automatically synced with the rates we
// actually charge (and the rationale copy under each tier).
import { PRICING_TIERS } from "../lib/pricing.js";

const GH_REPO = "https://github.com/Jamie2111/liquid-memory";

// =====================================================================
// HERO SLOGAN ROTATION
// ---------------------------------------------------------------------
// "Same AI. Smaller bill." is the canonical tagline carried on the
// deck, the one-pagers, the SEO description, and the footer
// wordmark. To keep the homepage feeling alive across return visits,
// the h1 picks one of these variants at random on each page load.
//
// Pattern: two short parallel clauses, second italicised. The anchor
// variant is at index 0 and is also the default fallback if the
// random pick ever fails.
//
// Hero slogans must be DEFENSIBLE. Liquid Memory's compression is
// fact-preserving but lossy at the document level, and we are explicit
// about that everywhere. Any new slogan added here must pass the test:
// would a customer reading the docs recognize this as the same product?
const HERO_SLOGANS = [
  { first: "Same AI.",         primary: "Smaller bill." },
  { first: "Same model.",      primary: "Smaller bill." },
  { first: "Smaller prompts.", primary: "Lower bills." },
  { first: "Fewer tokens.",    primary: "Same task." },
];

function pickHeroSlogan() {
  // SSR guard, the app is purely client-rendered today but this
  // keeps it safe in case anyone ever wraps Landing in a static-
  // export step.
  if (typeof window === "undefined") return HERO_SLOGANS[0];

  let lastIdx = -1;
  try {
    lastIdx = parseInt(sessionStorage.getItem("lm-slogan-idx") || "-1", 10);
  } catch (_) {
    // sessionStorage can throw in some privacy-locked browsers,
    // we just accept the possible-repeat in that case.
  }

  let idx = Math.floor(Math.random() * HERO_SLOGANS.length);
  // Don't show the same slogan twice in a row. Bounded re-roll
  // (max 8 attempts) so this can never spin forever even if the
  // RNG misbehaves.
  if (HERO_SLOGANS.length > 1) {
    let safety = 0;
    while (idx === lastIdx && safety++ < 8) {
      idx = Math.floor(Math.random() * HERO_SLOGANS.length);
    }
  }

  try {
    sessionStorage.setItem("lm-slogan-idx", String(idx));
  } catch (_) {
    /* see above */
  }
  return HERO_SLOGANS[idx];
}

export default function Landing() {
  const [tab, setTab] = useState("py");
  const [copied, setCopied] = useState(false);
  const { isAuthenticated, isAdmin } = useAuth();

  // Pick a hero slogan once per mount. Lazy initialiser so we only
  // call pickHeroSlogan (and touch sessionStorage) on the first
  // render, not on every re-render the auth context triggers.
  const [heroSlogan] = useState(pickHeroSlogan);

  const dashTo = isAuthenticated ? (isAdmin ? "/admin" : "/portal") : "/login?mode=signup";
  const dashLabel = isAuthenticated ? (isAdmin ? "Open admin" : "Open dashboard") : "Create an account";

  const copyCurrentSnippet = async () => {
    const node = document.getElementById("code-" + tab);
    if (!node) return;
    try {
      await navigator.clipboard.writeText(node.innerText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked, silently ignore */
    }
  };

  return (
    <div id="landing-view">
      <SEO
        description="Liquid Memory. A drop-in proxy that compresses long-context LLM prompts on your own machines before forwarding to OpenAI, Anthropic, or Google. Extractive and byte-exact on the code it keeps. On SWE-bench Verified it matches direct task success with no quality loss. One line of code, no lock-in, and you pay only on the spend it provably removes."
        path="/"
      />
      <SiteNav />

      <div className="page">
        <div className="topline"></div>
        <div className="meta">
          <span>01, In short</span>
        </div>

        {/* HERO */}
        <section className="hero">
          <div className="eyebrow">For teams running AI agents at scale</div>
          <h1 className="display">
            {heroSlogan.first}<br />
            <em>{heroSlogan.primary}</em>
          </h1>
          <p className="sub">
            The drop-in proxy that makes your coding agents cheaper without
            changing what they do. One line, same results, provably faithful.
          </p>
          {/* CTA hierarchy: try-it-now first. The playground is the
              highest-converting CTA (no signup, no install, it runs in
              the browser). GitHub is the secondary path for the
              engineer who wants to read the docs. Authenticated users
              (returning customers) see their dashboard CTA. */}
          <div className="cta-row">
            {isAuthenticated ? (
              <Link to={dashTo} className="btn btn-primary">
                {dashLabel} <span className="arr" aria-hidden="true">→</span>
              </Link>
            ) : (
              <Link to="/login?mode=signin" className="btn btn-primary">
                Try it now <span className="arr" aria-hidden="true">→</span>
              </Link>
            )}
            <a
              href={GH_REPO}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost"
            >
              <span className="arr" aria-hidden="true">↳</span> View on GitHub
            </a>
          </div>
          <div className="hero-foot">
            <span><b>{HEADLINE_BILL_REDUCTION_PCT}%</b> Lower Bills</span>
            <span><b>byte-exact</b> fidelity</span>
            <span><b>pay</b> only on savings</span>
          </div>
        </section>

        {/* PROOF */}
        <section className="block" id="proof">
          <div className="sec-label">
            <span>
              <span className="num">02</span> &nbsp;&nbsp;The Proof
            </span>
            <span>SWE-bench Verified · agentic coding</span>
          </div>

          <h2 className="sec">Your agents don't notice. Your bill does.</h2>
          <p className="sec-lede">
            We proved it on the hardest public coding benchmark, then
            guaranteed the rest by construction. Not a promise, a property.
          </p>
          <div className="metrics metrics-3">
            <div className="metric">
              <div className="tag">You lose nothing</div>
              <div className="v">60<sup>/98</sup></div>
              <div className="lbl">
                tasks solved through Liquid Memory on SWE-bench Verified,
                against 57 of 99 going direct. Your agents do the same
                work, or a little more.
              </div>
            </div>
            <div className="metric">
              <div className="tag">Nothing gets garbled</div>
              <div className="v">100<sup>%</sup></div>
              <div className="lbl">
                of the code we keep is byte-for-byte yours. A path, a
                number, a name, never rewritten, so nothing is silently
                corrupted.
              </div>
            </div>
            <div className="metric">
              <div className="tag">Your bill drops</div>
              <div className="v">80<sup>%</sup></div>
              <div className="lbl">
                Most of what your agent sends is repo context and history
                the model barely reads. We strip it before it leaves your
                network, so you pay for a fraction of the tokens.
              </div>
            </div>
          </div>
          <p style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 18, maxWidth: 560 }}>
            And it never costs more than going direct: if a call wouldn't save
            enough, your full prompt is sent untouched.
          </p>
        </section>

        {/* CODE */}
        <section className="block" id="code">
          <div className="sec-label">
            <span>
              <span className="num">03</span> &nbsp;&nbsp;The Drop-in
            </span>
            <span>One line. Zero rewrites.</span>
          </div>
          <div className="code-wrap">
            <div className="code-aside">
              <h2 className="sec">Edit one line. Pay less.</h2>
              <p>
                Your code already sends prompts to OpenAI, Anthropic, or
                Gemini. Change <em>exactly one line</em>, the address those
                prompts go to, and every prompt is compressed on your own
                machines before it leaves your network. Same code, same
                model, smaller invoice. Reversible any time by changing
                that line back.
              </p>
              <div className="providers">
                <span className="prov">OpenAI</span>
                <span className="prov">Anthropic</span>
                <span className="prov">Gemini</span>
              </div>
              <div className="annot">
                <span>Setup, ~5 minutes</span>
                <span>Runs, on your machines</span>
              </div>
            </div>

            <div className="code-block" role="region" aria-label="Code example">
              <div className="code-tabs" role="tablist">
                {[
                  ["py", "Python"],
                  ["ts", "TypeScript"],
                  ["curl", "cURL"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className="tab"
                    role="tab"
                    aria-selected={tab === id}
                    onClick={() => setTab(id)}
                    data-tab={id}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="code-body">
                <button className="copy" type="button" onClick={copyCurrentSnippet}>
                  {copied ? "Copied" : "Copy"}
                </button>
                <div className="code-scroll">
                  <pre id="code-py" hidden={tab !== "py"} style={{ margin: 0 }}>
                    {pySnippet}
                  </pre>
                  <pre id="code-ts" hidden={tab !== "ts"} style={{ margin: 0 }}>
                    {tsSnippet}
                  </pre>
                  <pre id="code-curl" hidden={tab !== "curl"} style={{ margin: 0 }}>
                    {curlSnippet}
                  </pre>
                </div>
              </div>
              <div className="code-foot">
                <strong>How this stays private:</strong>{" "}
                <code>localhost:8000</code> is the Liquid Memory
                gateway running on <strong>your</strong> machines.
                Extraction runs locally, so your raw prompts and your
                provider API keys never reach Liquid Memory. Only the
                compressed pack continues to the LLM provider you
                already use.{" "}
                <strong>To run it:</strong>{" "}
                <code>pip install liquid-memory-proxy</code>, set your
                provider key, run <code>liquid-memory start</code>.
                Adds &lt; 40 ms p99 latency per call.
              </div>
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section className="block" id="pricing">
          <div className="sec-label">
            <span>
              <span className="num">04</span> &nbsp;&nbsp;The Pricing
            </span>
            <span>Pay only on what we save you.</span>
          </div>
          <h2 className="sec">Pay only on what we save you.</h2>
          <p className="sec-lede">
            We charge a commission on the inference spend we provably
            remove, and nothing else. No subscription, no per-seat, no
            minimums. If we don't save you money, you don't pay us. The
            rate steps down as your monthly savings step up, like
            income-tax brackets, so there are no cliffs and nothing to
            game.
          </p>

          <div className="pricing-tier-grid pricing-tier-grid-landing">
            {PRICING_TIERS.map((tier) => (
              <article key={tier.id} className="pricing-tier-card">
                <div className="pricing-tier-band">{tier.label}</div>
                <div className="pricing-tier-rate">{tier.rate_pct_label}</div>
                <div className="pricing-tier-rationale">{tier.rationale}</div>
              </article>
            ))}
          </div>

          <div className="pricing-landing-foot">
            <span className="pricing-landing-foot-line">
              <strong>How "savings" is measured:</strong> for every
              request we log the tokens your raw document would have
              sent vs. the tokens our compressed pack actually sent. The
              difference, priced at your provider's published per-token
              rate, is the measured saving. Polar (Merchant of Record)
              handles invoicing and global tax compliance.
            </span>
            <Link to="/pricing" className="btn btn-ghost pricing-landing-cta">
              See worked examples <span className="arr" aria-hidden="true">→</span>
            </Link>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="block final-cta">
          <div className="final-cta-inner">
            <h2 className="sec">Ready to wire it in?</h2>
            <div className="cta-row">
              <Link to={isAuthenticated ? dashTo : "/login?mode=signin"} className="btn btn-primary">
                Try it now <span className="arr" aria-hidden="true">→</span>
              </Link>
              {!isAuthenticated && (
                <Link to="/login?mode=signup" className="btn btn-ghost">
                  Create an account <span className="arr" aria-hidden="true">→</span>
                </Link>
              )}
            </div>
          </div>
        </section>

        <footer className="site">
          <div className="lhs">
            <span className="wordmark">
              <img src="/icon.svg" className="wordmark-mark" alt="" width="22" height="22" />
              Liquid Memory
            </span>
            <p>
              Compiled context infrastructure for teams paying
              frontier-scale AI bills.
            </p>
          </div>
          <div className="rhs">
            <div
              className="row"
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "10.5px",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "var(--ink)",
                marginBottom: "6px",
              }}
            >
              Contact us
            </div>
            <div className="row">
              <a href="mailto:jamieobala2028@u.northwestern.edu">
                jamieobala2028@u.northwestern.edu
              </a>
            </div>
            <div className="row">
              <a href="mailto:selinasun2028@u.northwestern.edu">
                selinasun2028@u.northwestern.edu
              </a>
            </div>
            <div className="row" style={{ marginTop: "14px" }}>
              <Link to="/pricing">Pricing</Link> ·{" "}
              <Link to="/playground">Playground</Link> ·{" "}
              <Link to="/benchmarks">Benchmarks</Link> ·{" "}
              <Link to="/sla">Faithfulness</Link> ·{" "}
              <a href={GH_REPO} target="_blank" rel="noopener noreferrer">
                GitHub
              </a>
            </div>
            <div className="row">
              <Link to="/privacy-policy">Privacy</Link> ·{" "}
              <Link to="/terms-of-service">Terms</Link> ·{" "}
              <Link to="/changelog">Changelog</Link>
            </div>
            <div className="row">© Liquid Memory, All Rights Reserved</div>
          </div>
        </footer>
      </div>
    </div>
  );
}

// Code snippets shown in the tabbed example.
const pySnippet = `from openai import OpenAI

client = OpenAI(
    base_url="http://localhost:8000/v1",  # only change
)

response = client.chat.completions.create(
    model="gpt-4.1",
    messages=[
        {"role": "user", "content": docs},
        {"role": "user", "content": question},
    ],
)`;

const tsSnippet = `import OpenAI from "openai";

const client = new OpenAI({
  baseURL: "http://localhost:8000/v1",  // only change
});

const r = await client.chat.completions.create({
  model: "gpt-4.1",
  messages: [{ role: "user", content: docs }],
});`;

const curlSnippet = `curl http://localhost:8000/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -d '{
        "model": "gpt-4.1",
        "messages": [
          { "role": "user", "content": "<your long document>" },
          { "role": "user", "content": "What are the key risks?" }
        ]
      }'`;
